import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import usePrior from '@/hooks/use-prior.hook';
import { DRAWER_ANIMATE_DURATION, DrawerProps } from '@/components/drawers/helpers';
import useUserControl from '@/hooks/use-user-control.hook';
import { useSearch, useLocation } from '@tanstack/react-router';
import {
  clearUrlParams,
  DrawerConfig,
  DrawerContext,
  drawerMap,
  DrawerType,
  getUrlParams,
  setUrlParams,
} from './helpers';
import { cloneDeep } from 'lodash';
import { LoadState } from '@/lib/helpers';
import { TabVariant } from '@/hooks/use-tabs.hook';

interface Props {
  children: ReactNode;
}

export const DrawerProvider: FC<Props> = ({ children }) => {
  const [loadState, setLoadState] = useState<LoadState>('unloaded');
  const [stack, setStack] = useState<DrawerConfig[]>([]);

  const { checkLockedAndEmit } = useUserControl();

  // create a stable reference to the latest version of handleClose
  const handleCloseRef = useRef<(force?: boolean) => false | void>(() => false);

  const handleBack = useCallback(
    (force?: boolean) => {
      if (!force && checkLockedAndEmit('drawer')) {
        return false;
      }

      let priorDrawer: DrawerConfig | null = null;

      // close just the top drawer, leaving the rest untouched
      setStack((old) => {
        if (old.length < 2) {
          return old;
        }

        const topIndex = old.length - 1;
        priorDrawer = old[topIndex - 1];
        const updated = cloneDeep(old);

        updated[topIndex].props.open = false;
        updated[topIndex].closing = 'self';
        updated[topIndex].onClose();

        return updated;
      });

      // set URL params to prior drawer
      if (priorDrawer) {
        const { drawerType, props: drawerProps } = priorDrawer;
        setUrlParams(drawerType, drawerProps);
      }
    },
    [checkLockedAndEmit]
  );

  const handleClose = useCallback(
    (force?: boolean) => {
      if (!force && checkLockedAndEmit('drawer')) {
        return false;
      }

      setStack((old) => {
        if (!old.length) {
          return old;
        }

        // all but the top drawer should be immediately removed, while the top drawer animates closing
        const topIndex = old.length - 1;
        const updated = cloneDeep(old).map((drawer, index) => {
          drawer.props.open = false;
          drawer.onClose();

          // drawers lower down in the stack don't get closing animations, they just disappear
          if (index !== topIndex) {
            drawer.mounted = false;
            return drawer;
          }

          drawer.closing = 'all';

          return drawer;
        });

        return updated;
      });

      clearUrlParams();
    },
    [checkLockedAndEmit]
  );

  const handleOpen = useCallback((drawerType: DrawerType, drawerProps?: Partial<DrawerProps>) => {
    const { persistToUrl = true, ...restDrawerProps } = drawerProps || {};

    // Set URL params FIRST before mounting to ensure RouterTabs reads the correct tab (skip when persistToUrl is false)
    if (persistToUrl) {
      setUrlParams(drawerType, restDrawerProps, true);
    }

    setStack((old) => {
      const DrawerEl = drawerMap.get(drawerType);

      if (!DrawerEl) {
        return old;
      }

      const updated = cloneDeep(old);

      // @todo when useCallback redefined handleClose, the references in the configs are NOT being updated
      const drawerConfig = {
        drawerType,
        props: {
          ...cloneDeep(restDrawerProps),
          onClose: (force?: boolean) => handleCloseRef.current(force),
          open: false,
        },
        mounted: true,
        onClose: () => {
          if (typeof drawerProps?.onClose === 'function') {
            drawerProps.onClose();
          }
        },
      };

      updated.push(drawerConfig);

      return updated;
    });
  }, []);

  useHotkeys(
    'esc',
    () => {
      handleClose();
    },
    [handleClose]
  );

  // Keep handleCloseRef updated with the latest version of handleClose, so drawer configs don't have a stale reference
  useEffect(() => {
    handleCloseRef.current = handleClose;
  }, [handleClose]);

  /*
   * Manage drawer open/close state based on config stack changes.
   *
   * Why isn't this state just directly modified when calling handleOpen or handleClose? Because
   * drawers are not mounted unless they are in use, so on open we need to first mount, wait for
   * render, then open. And on close, we need to wait for the close animation before unmounting.
   * A better approach than this expensive, ugly useEffect call would be adding a prop to
   * react-modern-drawer that automatically triggers this behavior on open/close.
   */
  useEffect(() => {
    if (!stack.length) {
      return;
    }

    const closingAll = stack.some(({ closing }) => closing === 'all');

    if (closingAll) {
      setTimeout(() => {
        setStack([]);
      }, DRAWER_ANIMATE_DURATION + 10);

      return;
    }

    const closingIndex = stack.findIndex(({ closing }) => closing === 'self');

    if (closingIndex > -1) {
      setTimeout(() => {
        setStack((old) => {
          const updated = cloneDeep(old);
          updated.splice(closingIndex, 1);
          return updated;
        });
      }, DRAWER_ANIMATE_DURATION + 10);

      return;
    }

    const openIndexes: number[] = [];

    stack.forEach(({ mounted, props: { open } }, index) => {
      if (mounted && !open) {
        openIndexes.push(index);
      }
    });

    if (openIndexes.length) {
      setStack((old) => {
        const updated = cloneDeep(old);
        openIndexes.forEach((openIndex) => {
          updated[openIndex].props.open = true;
        });
        return updated;
      });
    }
  }, [stack]);

  // on page load, re-open a drawer from url values
  useEffect(() => {
    if (loadState !== 'unloaded') {
      return;
    }

    setLoadState('loading');
  }, [loadState]);

  useEffect(() => {
    if (loadState !== 'loading') {
      return;
    }

    setLoadState('loaded');

    const { drawer, ...rest } = getUrlParams() || {};

    if (!drawer) {
      return;
    }

    handleOpen(drawer, rest);
  }, [loadState, handleOpen]);

  // watch for hash changes and sync state to make navigation work
  const search = useSearch({
    strict: false,
  });

  const searchParams = search as { drawer?: string; [TabVariant.drawer]: string };
  const drawerParam = searchParams.drawer;
  const priorDrawerParam = usePrior(drawerParam);
  const tabParam = searchParams[TabVariant.drawer];

  // Save the tab parameter to the drawer props so it can be restored on handleBack
  useEffect(() => {
    setStack((old) => {
      if (!old.length) {
        return old;
      }

      const updated = cloneDeep(old);
      updated[old.length - 1].props.tab = tabParam || undefined;

      return updated;
    });
  }, [tabParam]);

  // Did the querystring lose its drawer param? Close all open drawers
  useEffect(() => {
    if (priorDrawerParam && !drawerParam) {
      const open = stack.some(({ props: { open } }) => open);
      if (open) {
        handleClose();
      }
    }
  }, [priorDrawerParam, drawerParam, handleClose, stack]);

  // navigating away from the page? clear drawer params
  const { pathname } = useLocation();
  const priorPathname = usePrior(pathname);
  useEffect(() => {
    if (drawerParam && priorPathname && priorPathname !== pathname) {
      clearUrlParams();
    }
  }, [pathname, priorPathname, drawerParam]);

  const settings = {
    openDrawer: handleOpen,
    closeDrawer: (force?: boolean) => {
      handleClose(force);
    },
    goBackDrawer: handleBack,
    stack,
  };

  return <DrawerContext.Provider value={settings}>{children}</DrawerContext.Provider>;
};
